export type KnowledgeSourceType =
  | "document"
  | "task"
  | "phase"
  | "daily_log"
  | "expense"
  | "quote"
  | "contact"
  | "fact"
  | "material";

/** Nem pinnelhető, mindig elérhető kontextus-források (napló, pénz, tények). */
export const RECORD_SOURCE_TYPES: readonly KnowledgeSourceType[] = [
  "daily_log",
  "expense",
  "quote",
  "contact",
  "fact"
];

export type RankedChunk = {
  id?: string;
  source_type: KnowledgeSourceType;
  source_id: string;
  title: string;
  chunk_index: number;
  content: string;
  category: string | null;
  rank?: number;
};

export type SourcePin = {
  /** Üres + inaktív pin → minden forrás. Ha a felhasználó pipál, csak a kijelöltek. */
  documentIds: string[];
  includeTasks: boolean;
  includePhases: boolean;
};

export const DEFAULT_PIN: SourcePin = {
  documentIds: [],
  includeTasks: false,
  includePhases: false
};

export function isPinActive(pin: SourcePin): boolean {
  return pin.documentIds.length > 0 || pin.includeTasks || pin.includePhases;
}

export const CATEGORY_BOOST: Record<string, number> = {
  permit: 3,
  plan: 3,
  contract: 2
};

export const CONTEXT_CHAR_BUDGET = 14_000;

export function sanitizeSearchQuery(raw: string): string {
  return raw
    .replace(/[%_\\,()]/g, " ")
    .replace(/\s+/g, " ")
    .trim()
    .slice(0, 200);
}

function scoreChunk(chunk: RankedChunk, query: string): number {
  const q = query.toLowerCase();
  const words = q.split(/\s+/).filter((w) => w.length > 1);
  const title = chunk.title.toLowerCase();
  const content = chunk.content.toLowerCase();
  let score = chunk.rank ?? 0;
  if (q && title.includes(q)) score += 5;
  for (const w of words) {
    if (title.includes(w)) score += 2;
    if (content.includes(w)) score += 0.5;
  }
  const boost = chunk.category ? CATEGORY_BOOST[chunk.category] : undefined;
  if (boost) score += boost;
  return score;
}

/** FTS + ilike találatok összefésülése, kategória-boost, cím-egyezés. */
export function rankChunks(chunks: RankedChunk[], query: string): RankedChunk[] {
  const seen = new Set<string>();
  const unique: RankedChunk[] = [];
  for (const c of chunks) {
    const key = `${c.source_type}:${c.source_id}:${c.chunk_index}`;
    if (seen.has(key)) continue;
    seen.add(key);
    unique.push(c);
  }
  const q = sanitizeSearchQuery(query).toLowerCase();
  return unique
    .map((c) => ({ ...c, rank: scoreChunk(c, q) }))
    .sort((a, b) => (b.rank ?? 0) - (a.rank ?? 0) || a.chunk_index - b.chunk_index);
}

export function filterChunksByPin(chunks: RankedChunk[], pin: SourcePin): RankedChunk[] {
  if (!isPinActive(pin)) return chunks;
  const docSet = new Set(pin.documentIds);
  return chunks.filter((c) => {
    if (c.source_type === "document") return docSet.has(c.source_id);
    if (c.source_type === "task") return pin.includeTasks;
    if (c.source_type === "phase" || c.source_type === "material") return pin.includePhases;
    return false;
  });
}

/** Spine után a maradék karakterkeret; pinnelt / magas rank előrébb. */
export function trimToBudget(chunks: RankedChunk[], budget = CONTEXT_CHAR_BUDGET): RankedChunk[] {
  const out: RankedChunk[] = [];
  let used = 0;
  for (const c of chunks) {
    const len = c.content.length + c.title.length + 40;
    if (out.length > 0 && used + len > budget) break;
    out.push(c);
    used += len;
  }
  return out;
}

export function uniqueCitations(chunks: RankedChunk[]): Array<{
  source_type: KnowledgeSourceType;
  source_id: string;
  title: string;
}> {
  const seen = new Set<string>();
  const out: Array<{ source_type: KnowledgeSourceType; source_id: string; title: string }> = [];
  for (const c of chunks) {
    const key = `${c.source_type}:${c.source_id}`;
    if (seen.has(key)) continue;
    seen.add(key);
    out.push({ source_type: c.source_type, source_id: c.source_id, title: c.title });
  }
  return out;
}
